<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from 'vue'
  import { useUserStore } from '@/store/userStore'
  import { storeToRefs } from 'pinia'
  import { Calendar, Clock, Rocket, CheckCircle2 } from 'lucide-vue-next'

  const userStore = useUserStore()
  const { user } = storeToRefs(userStore)

  // Current time updated every second
  const now = ref(new Date())
  let intervalId: ReturnType<typeof setInterval> | null = null

  onMounted(async () => {
    await userStore.fetchUser()
    intervalId = setInterval(() => {
      now.value = new Date()
    }, 1000)
  })

  onUnmounted(() => {
    if (intervalId) {
      clearInterval(intervalId)
    }
  })

  // Parse the onboarding date
  const onboardingDate = computed(() => {
    if (!user.value?.org?.onboarding_start_date) return null
    return new Date(user.value.org.onboarding_start_date)
  })

  // Calculate time remaining
  const timeRemaining = computed(() => {
    if (!onboardingDate.value) return null

    const diff = onboardingDate.value.getTime() - now.value.getTime()

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, isReady: true }
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)

    return { days, hours, minutes, seconds, isReady: false }
  })

  // Format the onboarding date for display
  const formattedDate = computed(() => {
    if (!onboardingDate.value) return ''
    return new Intl.DateTimeFormat('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(onboardingDate.value)
  })

  // Pad numbers with leading zeros
  const pad = (num: number) => num.toString().padStart(2, '0')
</script>

<template>
  <div
    class="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900"
  >
    <!-- Animated background elements -->
    <div class="absolute inset-0 overflow-hidden">
      <!-- Floating orbs -->
      <div
        class="absolute left-1/4 top-1/4 h-96 w-96 animate-pulse rounded-full bg-purple-500/20 blur-3xl"
      />
      <div
        class="absolute bottom-1/4 right-1/4 h-80 w-80 animate-pulse rounded-full bg-blue-500/20 blur-3xl
          delay-1000"
      />
      <div
        class="absolute right-1/3 top-1/2 h-64 w-64 animate-pulse rounded-full bg-pink-500/10 blur-3xl delay-500"
      />

      <!-- Grid pattern overlay -->
      <div
        class="absolute inset-0 opacity-10"
        style="
          background-image:
            linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
          background-size: 50px 50px;
        "
      />
    </div>

    <!-- Main content -->
    <div class="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 py-12">
      <!-- Logo / Brand -->
      <div class="mb-8 flex items-center gap-4">
        <img
          src="/images/logo-white.svg"
          alt="JUPUS Logo"
          class="h-16 w-16"
        />
        <span class="text-4xl font-bold tracking-tight text-white">JUPUS</span>
      </div>

      <!-- Welcome message -->
      <div class="mb-12 text-center">
        <h1 class="mb-4 text-4xl font-bold text-white md:text-5xl">
          Willkommen, {{ user?.org?.name }}!
        </h1>
        <p class="mx-auto max-w-xl text-lg text-purple-200/80">
          Ihr JUPUS Account wurde erfolgreich eingerichtet. Wir freuen uns darauf, Sie bei Ihrem
          Onboarding zu begrüßen.
        </p>
      </div>

      <!-- Countdown or Ready State -->
      <div
        v-if="timeRemaining"
        class="mb-12"
      >
        <!-- Ready state -->
        <div
          v-if="timeRemaining.isReady"
          class="text-center"
        >
          <div
            class="mb-6 inline-flex items-center gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/20 px-8
              py-4"
          >
            <CheckCircle2 class="h-8 w-8 text-emerald-400" />
            <span class="text-2xl font-semibold text-emerald-300">Ihr Account ist bereit!</span>
          </div>
          <div class="mt-6">
            <a
              href="/app/cases"
              class="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 px-8 py-4
                font-semibold text-white shadow-lg shadow-purple-500/30 transition-all duration-300 hover:scale-105
                hover:shadow-purple-500/50"
            >
              <Rocket class="h-5 w-5" />
              Jetzt starten
            </a>
          </div>
        </div>

        <!-- Countdown -->
        <div v-else>
          <div class="mb-6 text-center">
            <p class="text-lg text-purple-300/70">Ihr Onboarding startet in</p>
          </div>

          <div class="flex items-center justify-center gap-4 md:gap-6">
            <!-- Days -->
            <div class="flex flex-col items-center">
              <div
                class="flex h-24 w-20 items-center justify-center rounded-2xl border border-white/10 bg-white/5 shadow-xl
                  backdrop-blur-xl md:h-32 md:w-28"
              >
                <span class="font-mono text-4xl font-bold text-white md:text-5xl">
                  {{ pad(timeRemaining.days) }}
                </span>
              </div>
              <span class="mt-3 text-sm uppercase tracking-widest text-purple-300/60">Tage</span>
            </div>

            <span class="mt-[-1.5rem] text-3xl font-light text-purple-400/50">:</span>

            <!-- Hours -->
            <div class="flex flex-col items-center">
              <div
                class="flex h-24 w-20 items-center justify-center rounded-2xl border border-white/10 bg-white/5 shadow-xl
                  backdrop-blur-xl md:h-32 md:w-28"
              >
                <span class="font-mono text-4xl font-bold text-white md:text-5xl">
                  {{ pad(timeRemaining.hours) }}
                </span>
              </div>
              <span class="mt-3 text-sm uppercase tracking-widest text-purple-300/60">Stunden</span>
            </div>

            <span class="mt-[-1.5rem] text-3xl font-light text-purple-400/50">:</span>

            <!-- Minutes -->
            <div class="flex flex-col items-center">
              <div
                class="flex h-24 w-20 items-center justify-center rounded-2xl border border-white/10 bg-white/5 shadow-xl
                  backdrop-blur-xl md:h-32 md:w-28"
              >
                <span class="font-mono text-4xl font-bold text-white md:text-5xl">
                  {{ pad(timeRemaining.minutes) }}
                </span>
              </div>
              <span class="mt-3 text-sm uppercase tracking-widest text-purple-300/60">Minuten</span>
            </div>

            <span class="mt-[-1.5rem] text-3xl font-light text-purple-400/50">:</span>

            <!-- Seconds -->
            <div class="flex flex-col items-center">
              <div
                class="relative flex h-24 w-20 items-center justify-center overflow-hidden rounded-2xl border
                  border-white/10 bg-white/5 shadow-xl backdrop-blur-xl md:h-32 md:w-28"
              >
                <span class="relative z-10 font-mono text-4xl font-bold text-white md:text-5xl">
                  {{ pad(timeRemaining.seconds) }}
                </span>
                <!-- Pulsing effect on seconds -->
                <div
                  class="absolute inset-0 animate-pulse bg-gradient-to-t from-purple-500/10 to-transparent"
                />
              </div>
              <span class="mt-3 text-sm uppercase tracking-widest text-purple-300/60">
                Sekunden
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- No onboarding date set -->
      <div
        v-else-if="user"
        class="mb-12 text-center"
      >
        <div
          class="inline-flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/20 px-6 py-3"
        >
          <Clock class="h-5 w-5 text-amber-400" />
          <span class="text-amber-300">Onboarding-Termin wird noch festgelegt</span>
        </div>
      </div>

      <!-- Date display card -->
      <div
        v-if="onboardingDate && !timeRemaining?.isReady"
        class="rounded-2xl border border-white/10 bg-white/5 px-8 py-6 shadow-xl backdrop-blur-xl"
      >
        <div class="flex items-center gap-4">
          <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-500/20">
            <Calendar class="h-6 w-6 text-purple-400" />
          </div>
          <div>
            <p class="mb-1 text-sm text-purple-300/60">Onboarding-Termin</p>
            <p class="text-xl font-semibold text-white">{{ formattedDate }}</p>
          </div>
        </div>
      </div>

      <!-- Features preview -->
      <div class="mt-16 grid w-full max-w-4xl grid-cols-1 gap-6 md:grid-cols-3">
        <div class="rounded-xl border border-white/10 bg-white/5 p-6 text-center backdrop-blur-sm">
          <div
            class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500/20"
          >
            <svg
              class="h-6 w-6 text-blue-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 class="mb-2 font-semibold text-white">Intelligente Aktenführung</h3>
          <p class="text-sm text-purple-300/60">
            KI-gestützte Dokumentenanalyse und automatische Kategorisierung
          </p>
        </div>

        <div class="rounded-xl border border-white/10 bg-white/5 p-6 text-center backdrop-blur-sm">
          <div
            class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/20"
          >
            <svg
              class="h-6 w-6 text-emerald-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h3 class="mb-2 font-semibold text-white">KI-Assistent</h3>
          <p class="text-sm text-purple-300/60">
            Ihr persönlicher Rechtsassistent für Analysen und Recherche
          </p>
        </div>

        <div class="rounded-xl border border-white/10 bg-white/5 p-6 text-center backdrop-blur-sm">
          <div
            class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-pink-500/20"
          >
            <svg
              class="h-6 w-6 text-pink-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
          </div>
          <h3 class="mb-2 font-semibold text-white">Phonebot</h3>
          <p class="text-sm text-purple-300/60">KI-gestützte Telefonannahme für Ihre Kanzlei</p>
        </div>
      </div>

      <!-- Contact info -->
      <div class="mt-12 text-center">
        <p class="text-sm text-purple-300/50">
          Fragen? Kontaktieren Sie uns unter
          <a
            href="mailto:support@jupus.de"
            class="text-purple-400 underline underline-offset-2 hover:text-purple-300"
          >
            support@jupus.de
          </a>
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
  @keyframes pulse {
    0%,
    100% {
      opacity: 0.4;
      transform: scale(1);
    }
    50% {
      opacity: 0.6;
      transform: scale(1.05);
    }
  }

  .animate-pulse {
    animation: pulse 4s ease-in-out infinite;
  }

  .delay-500 {
    animation-delay: 0.5s;
  }

  .delay-1000 {
    animation-delay: 1s;
  }

  /* Smooth number transitions */
  .font-mono {
    font-variant-numeric: tabular-nums;
  }
</style>
